/**
 * The billing reports (usage dashboard, invoice so far, deferred revenue) as the portal
 * asks for them, kept out of the pages so they can be tested.
 *
 * Usage and deferred revenue take IST days / months as they are; the invoice preview's
 * `before` is a server UTC LocalDateTime, so an IST cut-off goes through istToServerUtc.
 */
import { DailyUsage, ServiceType, serviceTypeLabels } from '../../types/billing';
import { addDays, istToday, istToServerUtc } from './closeBooksRules';

/**
 * Platform staff: the roles the backend treats as platform-wide (isMasterAdmin ||
 * isPlatformAdmin). They pick the organisation; everyone else reads their own.
 */
const PLATFORM_STAFF = ['platform_admin', 'master_admin'];
export const isPlatformStaff = (role: string | null | undefined): boolean =>
  PLATFORM_STAFF.includes((role || '').toLowerCase());

/**
 * Who sees the deferred-revenue page: platform staff only (2026-10-14 audit).
 *
 * The backend also admits provider roles, but this page offers a provider no picker, so it
 * showed the provider's OWN wallet at Green Giraffe — GG's liability, not the provider's
 * deferred revenue (which is its businesses' prepaid wallets). Until the page can list a
 * provider's businesses it is not shown to them; their wallet is on My billing.
 */
export const DEFERRED_REVENUE_ROLES = PLATFORM_STAFF;

/** Route roles of the platform-only billing pages (Overview, Credit Accounts). */
export const PLATFORM_BILLING_ROLES = PLATFORM_STAFF;
export const canSeeDeferredRevenue = (role: string | null | undefined): boolean =>
  DEFERRED_REVENUE_ROLES.includes((role || '').toLowerCase());

/** The server refuses a usage range longer than this many days. */
export const MAX_DASHBOARD_DAYS = 400;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Days from..to, both inclusive; 0 when either is not a date or to < from. */
export const daySpan = (from: string, to: string): number => {
  if (!YMD.test(from || '') || !YMD.test(to || '')) return 0;
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const n = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000) + 1;
  return n > 0 ? n : 0;
};

/** Why a usage range cannot be asked for, or null when it can. */
export const usageRangeError = (from: string, to: string): string | null => {
  if (!from || !to) return 'Pick both dates';
  const n = daySpan(from, to);
  if (n === 0) return 'The end is before the start';
  if (n > MAX_DASHBOARD_DAYS) return `At most ${MAX_DASHBOARD_DAYS} days at a time (this is ${n})`;
  return null;
};

/** The last 30 IST days, today included. */
export const last30Days = (now: Date = new Date()): { from: string; to: string } => {
  const to = istToday(now);
  return { from: addDays(to, -29), to };
};

/** Yesterday in IST: the daily statement's default day. */
export const istYesterday = (now: Date = new Date()): string => addDays(istToday(now), -1);

/** Blank → absent, so the server never parses an empty parameter. */
const compact = <T extends Record<string, unknown>>(o: T): Partial<T> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined)) as Partial<T>;

export const usageDashboardParams = (q: {
  organizationId?: string;
  from: string;
  to: string;
  businessId?: string;
}) => compact({ organizationId: q.organizationId, from: q.from, to: q.to, businessId: q.businessId });

/**
 * Invoice preview params. `beforeIst` is a `datetime-local` IST value, or blank for
 * "now" (the server's default). `taxPercent` blank → the server's 18%.
 */
export const invoicePreviewParams = (q: {
  organizationId: string;
  beforeIst?: string;
  taxPercent?: string | number;
}) =>
  compact({
    organizationId: q.organizationId,
    before: (q.beforeIst && istToServerUtc(q.beforeIst)) || undefined,
    taxPercent: q.taxPercent === '' || q.taxPercent === undefined
      ? undefined : Number(q.taxPercent),
  });

export const deferredRevenueParams = (q: { month: string; organizationId?: string }) =>
  compact({ month: q.month, organizationId: q.organizationId });

/** `VOICE_OUTBOUND` → `Voice Outbound`; a key the portal does not know is shown as sent. */
export const usageKeyLabel = (key?: string | null): string =>
  !key ? '-' : serviceTypeLabels[key as ServiceType] || key;

/** Minutes with up to two decimals, Indian grouping; '-' when absent. */
export const formatMinutes = (n?: number | null): string =>
  n === undefined || n === null
    ? '-'
    : new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);

export const formatCount = (n?: number | null): string =>
  new Intl.NumberFormat('en-IN').format(n ?? 0);

/**
 * Every IST day of from..to, with zero for a day that had no usage: the server lists
 * only days with charges, and a chart that skips the quiet days draws a false trend.
 */
export const fillDailySeries = (daily: DailyUsage[], from: string, to: string): DailyUsage[] => {
  const n = daySpan(from, to);
  if (n === 0 || n > MAX_DASHBOARD_DAYS) return daily;
  const byDay = new Map(daily.map((d) => [d.day, d]));
  return Array.from({ length: n }, (_, i) => {
    const day = addDays(from, i);
    return byDay.get(day) || { day, charges: 0, billedMinutes: 0, amount: 0 };
  });
};

/** `2026-10-07` → `07 Oct`, for a chart axis. No time zone: it is a calendar day. */
export const shortDay = (ymd: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return ymd;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString('en-IN', { timeZone: 'UTC', day: '2-digit', month: 'short' });
};
